/**
 * Loads `display_currency` from GET /api/notifications/preferences and refetches
 * when Display Preferences saves currency (`displayPreferencesChanged` with `displayCurrency`).
 */

import { useAuth } from "@clerk/clerk-react";
import { useCallback, useEffect, useState } from "react";
import { fetchNotificationPreferences } from "../services/notificationsApi";
import { DISPLAY_PREFERENCES_CHANGED_EVENT } from "./useUserNotificationTimezone";

export interface UseUserDisplayCurrencyResult {
  displayCurrency: string;
  supportedCurrencies: string[];
  isLoading: boolean;
  error: Error | null;
  /** Bumps after a successful load — use to refetch chart data that formats money. */
  revision: number;
  refetch: () => Promise<void>;
}

export function useUserDisplayCurrency(): UseUserDisplayCurrencyResult {
  const { isLoaded, isSignedIn } = useAuth();
  const [displayCurrency, setDisplayCurrency] = useState("USD");
  const [supportedCurrencies, setSupportedCurrencies] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [revision, setRevision] = useState(0);

  const load = useCallback(async () => {
    if (!isLoaded || !isSignedIn) {
      setDisplayCurrency("USD");
      setSupportedCurrencies([]);
      setError(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const prefs = await fetchNotificationPreferences();
      setDisplayCurrency((prefs.displayCurrency || "USD").toUpperCase());
      setSupportedCurrencies(prefs.supportedCurrencies ?? []);
      setRevision((r) => r + 1);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      setDisplayCurrency("USD");
      setSupportedCurrencies([]);
      setRevision((r) => r + 1);
    } finally {
      setIsLoading(false);
    }
  }, [isLoaded, isSignedIn]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onPrefs = (ev: Event) => {
      const detail = (ev as CustomEvent<{ setting?: string }>).detail;
      if (detail?.setting === "displayCurrency") {
        void load();
      }
    };
    window.addEventListener(DISPLAY_PREFERENCES_CHANGED_EVENT, onPrefs as EventListener);
    return () =>
      window.removeEventListener(DISPLAY_PREFERENCES_CHANGED_EVENT, onPrefs as EventListener);
  }, [load]);

  return {
    displayCurrency,
    supportedCurrencies,
    isLoading,
    error,
    revision,
    refetch: load,
  };
}
